import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  getRecurringTemplates,
  toggleRecurringTemplate,
  deleteRecurringTemplate,
} from '@/lib/firestore-recurring';
import { formatMoney } from '@/lib/utils';
import { useCategories } from '@/contexts/CategoriesContext';
import type { RecurringTemplate } from '@/types/recurring';
import {
  formatFrequency,
  getUpcomingOccurrences,
} from '@/lib/recurring-processor';
import { Trash2, Edit, Power, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

interface RecurringTransactionsListProps {
  onEdit: (template: RecurringTemplate) => void;
  onRefresh?: () => void;
}

export function RecurringTransactionsList({
  onEdit,
  onRefresh,
}: RecurringTransactionsListProps) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { base: displayCurrency } = useCurrency();
  const { getCategoryLabel: getCtxCategoryLabel } = useCategories();
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const language = settings?.language || 'en';
  const labels = {
    en: {
      paused: 'Paused',
      active: 'Active',
      amount: 'Amount',
      startDate: 'Start Date',
      endDate: 'End Date',
      upcoming: '📅 Upcoming Occurrences:',
      nextOccurrence: 'Next:',
      loading: 'Loading recurring transactions...',
      noRecurring: 'No Recurring Transactions',
      noRecurringMsg: 'Set up recurring transactions for bills, subscriptions, and regular income.',
      deleteConfirm: (name: string) =>
        `Are you sure you want to delete "${name}"? This will not delete already created transactions.`,
      failToggle: 'Failed to update recurring transaction',
      failDelete: 'Failed to delete recurring transaction',
      couldNotLoad: 'Could not load recurring transactions',
      retry: 'Retry',
      viewUpcoming: 'View upcoming',
      pause: 'Pause',
      activate: 'Activate',
      edit: 'Edit',
      delete: 'Delete',
    },
    fr: {
      paused: 'En pause',
      active: 'Actif',
      amount: 'Montant',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      upcoming: '📅 Prochaines occurrences :',
      nextOccurrence: 'Prochaine :',
      loading: 'Chargement des transactions récurrentes...',
      noRecurring: 'Aucune transaction récurrente',
      noRecurringMsg:
        'Configurez des transactions récurrentes pour les factures, abonnements et revenus réguliers.',
      deleteConfirm: (name: string) =>
        `Êtes-vous sûr de vouloir supprimer "${name}" ? Les transactions déjà créées ne seront pas supprimées.`,
      failToggle: 'Échec de la mise à jour de la transaction récurrente',
      failDelete: 'Échec de la suppression de la transaction récurrente',
      couldNotLoad: 'Impossible de charger les transactions récurrentes',
      retry: 'Réessayer',
      viewUpcoming: 'Voir les prochaines',
      pause: 'Mettre en pause',
      activate: 'Activer',
      edit: 'Modifier',
      delete: 'Supprimer',
    },
  };
  const lbl = labels[language as 'en' | 'fr'] || labels.en;

  const getCategoryLabel = (categoryId: string, _type: 'gain' | 'expense'): string =>
    getCtxCategoryLabel(categoryId);

  const loadTemplates = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getRecurringTemplates(user.uid);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading recurring templates:', error);
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes('index')) {
        setLoadError(
          'A Firestore index is missing. Check the browser console for a link to create it, or deploy firestore.indexes.json.'
        );
      } else {
        setLoadError(`Failed to load: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [user]);

  const handleToggle = async (templateId: string, currentStatus: boolean) => {
    try {
      await toggleRecurringTemplate(templateId, !currentStatus);
      await loadTemplates();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error toggling template:', error);
      alert(lbl.failToggle);
    }
  };

  const handleDelete = async (templateId: string, name: string) => {
    if (!confirm(lbl.deleteConfirm(name))) return;
    try {
      await deleteRecurringTemplate(templateId);
      await loadTemplates();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(lbl.failDelete);
    }
  };

  const toggleExpanded = (templateId: string) => {
    setExpandedId(expandedId === templateId ? null : templateId);
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="mb-4 animate-pulse text-5xl">🔄</div>
        <p className="text-slate-600 dark:text-slate-400">{lbl.loading}</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/80 p-8 text-center shadow-xl backdrop-blur-xl dark:border-red-900 dark:bg-red-900/20">
        <div className="mb-4 text-5xl">⚠️</div>
        <h3 className="mb-2 text-lg font-bold text-red-800 dark:text-red-200">
          {lbl.couldNotLoad}
        </h3>
        <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
        <button
          onClick={loadTemplates}
          className="mt-4 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
        >
          {lbl.retry}
        </button>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 text-center shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
        <div className="mb-4 text-5xl">🔄</div>
        <h3 className="mb-2 text-xl font-bold text-slate-900 dark:text-slate-100">
          {lbl.noRecurring}
        </h3>
        <p className="text-slate-600 dark:text-slate-400">{lbl.noRecurringMsg}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {templates.map((template) => {
        const isExpanded = expandedId === template.id;
        const upcomingDates = isExpanded ? getUpcomingOccurrences(template, 3) : [];
        const nextDate = getUpcomingOccurrences(template, 1)[0];

        return (
          <div
            key={template.id}
            className={`rounded-2xl border border-slate-200 bg-white/80 shadow-lg backdrop-blur-xl transition-all dark:border-slate-700 dark:bg-slate-800/80 ${
              !template.isActive ? 'opacity-60' : ''
            }`}
          >
            <div className="p-4 sm:p-6">
              {/* ── Top row: icon + name + badge + actions ── */}
              <div className="flex items-start gap-3">
                {/* Icon */}
                <span className="mt-0.5 shrink-0 text-2xl">
                  {template.type === 'gain' ? '💰' : '💳'}
                </span>

                {/* Name + category + frequency */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 sm:text-lg">
                      {template.name}
                    </h3>
                    {!template.isActive && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {lbl.paused}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
                    {getCategoryLabel(template.categoryId, template.type)} •{' '}
                    {formatFrequency(template, language as 'en' | 'fr')}
                  </p>
                </div>

                {/* Action buttons — always visible, compact on mobile */}
                <div className="flex shrink-0 gap-1 sm:gap-2">
                  <button
                    onClick={() => toggleExpanded(template.id)}
                    className="rounded-lg bg-slate-100 p-2 text-slate-600 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                    title={lbl.viewUpcoming}
                    aria-label={lbl.viewUpcoming}
                  >
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <button
                    onClick={() => handleToggle(template.id, template.isActive)}
                    className={`rounded-lg p-2 transition-all ${
                      template.isActive
                        ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                    }`}
                    title={template.isActive ? lbl.pause : lbl.activate}
                    aria-label={template.isActive ? lbl.pause : lbl.activate}
                  >
                    <Power size={16} />
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="rounded-lg bg-blue-100 p-2 text-blue-600 transition-all hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400"
                    title={lbl.edit}
                    aria-label={lbl.edit}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id, template.name)}
                    className="rounded-lg bg-red-100 p-2 text-red-600 transition-all hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400"
                    title={lbl.delete}
                    aria-label={lbl.delete}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* ── Stats row: amount / start date / next occurrence / end date ── */}
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-3">
                {/* Amount */}
                <div className="min-w-[80px]">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{lbl.amount}</p>
                  <p
                    className={`text-lg font-bold ${
                      template.type === 'gain' ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {template.type === 'gain' ? '+' : '-'}
                    {formatMoney(template.amount, displayCurrency)}
                  </p>
                </div>

                {/* Start Date */}
                <div className="min-w-[80px]">
                  <p className="text-xs text-slate-500 dark:text-slate-400">{lbl.startDate}</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {template.startDate.toLocaleDateString(
                      language === 'fr' ? 'fr-FR' : 'en-US',
                      { month: 'short', day: 'numeric', year: 'numeric' }
                    )}
                  </p>
                </div>

                {/* Next occurrence */}
                {nextDate && template.isActive && (
                  <div className="min-w-[80px]">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lbl.nextOccurrence}</p>
                    <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      {nextDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                )}

                {/* End Date */}
                {template.endDate && (
                  <div className="min-w-[80px]">
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lbl.endDate}</p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {template.endDate.toLocaleDateString(
                        language === 'fr' ? 'fr-FR' : 'en-US',
                        { month: 'short', day: 'numeric', year: 'numeric' }
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* ── Expanded upcoming occurrences ── */}
              {isExpanded && upcomingDates.length > 0 && (
                <div className="mt-4 rounded-xl bg-slate-50 p-4 dark:bg-slate-700/50">
                  <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {lbl.upcoming}
                  </p>
                  <div className="space-y-1">
                    {upcomingDates.map((date, index) => (
                      <p key={index} className="text-sm text-slate-600 dark:text-slate-400">
                        •{' '}
                        {date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
